import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { recordPageVisit } from "@/lib/api";
import { 
  Heart, 
  Coffee, 
  Moon, 
  Users,
  MessageCircle,
  Phone,
  BookOpen,
  Lightbulb,
  Shield,
  ArrowRight
} from "lucide-react";

const selfCareTips = [
  {
    icon: Coffee,
    title: "Take Regular Breaks",
    description: "Step away for even 5-10 minutes. A short walk or cup of tea can help reset your mind."
  },
  {
    icon: Moon,
    title: "Prioritize Sleep",
    description: "Rest when you can. Consider accepting help with overnight care if available."
  },
  {
    icon: Users,
    title: "Accept Help",
    description: "Let friends and family help with meals, errands, or sitting with your loved one."
  },
  {
    icon: Heart,
    title: "Honor Your Feelings",
    description: "It's okay to feel sad, frustrated, or even angry. These feelings are normal."
  }
];

const resources = [
  {
    title: "Family Caregiver Alliance",
    url: "https://www.caregiver.org",
    description: "Education, services, and advocacy for family caregivers"
  },
  {
    title: "AARP Caregiving Resource Center",
    url: "https://www.aarp.org/caregiving",
    description: "Tools, tips, and support for family caregivers"
  },
  {
    title: "National Alliance for Caregiving",
    url: "https://www.caregiving.org",
    description: "Research and resources on family caregiving"
  },
  {
    title: "Caregiver Action Network",
    url: "https://www.caregiveraction.org",
    description: "Peer support and education for caregivers"
  }
];

const faqItems = [
  {
    question: "How do I know if I'm experiencing caregiver burnout?",
    answer: `Signs of caregiver burnout include:
• Feeling exhausted, even after rest
• Getting sick more often
• Feeling like caregiving is the only thing in your life
• Becoming impatient or irritable
• Feeling hopeless or helpless
• Withdrawing from friends and activities you once enjoyed
• Changes in appetite or sleep patterns

If you're experiencing these symptoms, please reach out to your hospice social worker or contact us. You don't have to carry this alone.`
  },
  {
    question: "What is respite care and how do I get it?",
    answer: `Respite care gives you a break from caregiving. There are several types:

• In-home respite: A trained volunteer or aide comes to your home
• Inpatient respite: Your loved one stays at a hospice facility for up to 5 days (covered by Medicare)
• Informal respite: Friends or family members take over for a period

Contact your hospice team to arrange respite care. Most hospice programs include this benefit, and you should never feel guilty for using it.`
  },
  {
    question: "How do I talk to my loved one about their end-of-life wishes?",
    answer: `Starting these conversations can be challenging, but they're so important:

• Choose a calm, private moment
• Start with open-ended questions like "What matters most to you right now?"
• Listen more than you speak
• Don't push - it's okay to have multiple conversations
• Write down their wishes so you can honor them

Our chat assistant can help guide you through these conversations step by step.`
  },
  {
    question: "What should I do immediately after my loved one passes?",
    answer: `When your loved one passes:

1. There is no need to rush - take the time you need
2. Call the hospice team - they will come to your home
3. The hospice nurse will pronounce death and contact the appropriate authorities
4. The funeral home will be contacted when you're ready
5. Hospice bereavement services will reach out to support you

The hospice team handles most of the practical matters. Your only job is to be present and begin your grieving process.`
  },
  {
    question: "How can I involve children in the caregiving process?",
    answer: `Children can be involved in meaningful ways:

• Simple tasks like reading aloud, playing music, or holding hands
• Drawing pictures or making cards
• Just being present and talking about their day
• Age-appropriate explanations about what's happening

Be honest with children at their level of understanding. The hospice social worker can provide guidance on how to include children and support them through this process.`
  }
];

export default function CaregiverResources() {
  const { t } = useTranslation();
  
  useEffect(() => {
    recordPageVisit("caregiver").catch(console.error);
  }, []);

  return (
    <div className="min-h-screen" data-testid="caregiver-resources-page">
      {/* Hero */}
      <section className="hero-gradient section-padding pb-16">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="caption mb-4">For Caregivers</p>
              <h1 className="heading-1 text-foreground mb-6">
                You Matter Too
              </h1>
              <p className="body-lg text-muted-foreground mb-8">
                Caring for a loved one at end of life is one of the most meaningful - and 
                challenging - things you can do. We're here to support you every step of the way.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/chat" data-testid="caregiver-chat-link">
                  <Button className="btn-primary">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Talk to Someone
                  </Button>
                </Link>
                <Link to="/symptoms" data-testid="caregiver-symptoms-link">
                  <Button variant="outline" className="btn-secondary">
                    View Symptom Guides
                  </Button>
                </Link>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://images.pexels.com/photos/7551661/pexels-photo-7551661.jpeg"
                alt="Caregiver providing loving support"
                className="rounded-3xl shadow-hover w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Self-Care Tips */}
      <section className="section-padding bg-white" data-testid="self-care-section">
        <div className="container-max">
          <div className="text-center space-y-4 mb-12">
            <p className="caption">Self-Care</p>
            <h2 className="heading-2 text-foreground">Taking Care of Yourself</h2>
            <p className="body-base max-w-2xl mx-auto">
              You can't pour from an empty cup. Here are some ways to maintain your 
              own well-being while caring for your loved one.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {selfCareTips.map((tip, index) => {
              const Icon = tip.icon;
              return (
                <Card key={index} className="shadow-soft card-hover text-center" data-testid={`self-care-tip-${index}`}>
                  <CardContent className="pt-8 pb-6 px-6">
                    <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="font-serif text-lg font-medium text-foreground mb-2">
                      {tip.title}
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      {tip.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Caregiver FAQ */}
      <section className="section-padding bg-muted/30" data-testid="caregiver-faq-section">
        <div className="container-max">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1">
              <p className="caption mb-4">Common Questions</p>
              <h2 className="heading-2 text-foreground mb-4">
                Caregiver FAQ
              </h2>
              <p className="body-base mb-6">
                Answers to questions we hear from caregivers. If you don't find what 
                you're looking for, our AI assistant is here to help.
              </p>
              <Link to="/chat" data-testid="caregiver-faq-chat-link">
                <Button variant="outline" className="rounded-full">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Ask a Question
                </Button>
              </Link>
            </div>
            
            <div className="lg:col-span-2">
              <Card className="shadow-soft">
                <CardContent className="p-6">
                  <Accordion type="single" collapsible className="w-full" data-testid="caregiver-faq-accordion">
                    {faqItems.map((item, index) => (
                      <AccordionItem key={index} value={`item-${index}`}>
                        <AccordionTrigger className="text-left font-serif text-lg hover:text-primary" data-testid={`caregiver-faq-trigger-${index}`}>
                          {item.question}
                        </AccordionTrigger>
                        <AccordionContent className="text-muted-foreground whitespace-pre-line leading-relaxed" data-testid={`caregiver-faq-content-${index}`}>
                          {item.answer}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* External Resources */}
      <section className="section-padding bg-white" data-testid="external-resources-section">
        <div className="container-max">
          <div className="text-center space-y-4 mb-12">
            <p className="caption">Additional Support</p>
            <h2 className="heading-2 text-foreground">Helpful Resources</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6 max-w-4xl mx-auto">
            {resources.map((resource, index) => (
              <a 
                key={index}
                href={resource.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block"
                data-testid={`resource-link-${index}`}
              >
                <Card className="shadow-soft card-hover h-full">
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-xl bg-secondary/20 flex items-center justify-center flex-shrink-0">
                        <BookOpen className="w-5 h-5 text-secondary-foreground" />
                      </div>
                      <div>
                        <h3 className="font-medium text-foreground mb-1 group-hover:text-primary">
                          {resource.title}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          {resource.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-primary text-white">
        <div className="container-max text-center">
          <div className="max-w-2xl mx-auto space-y-6">
            <Shield className="w-16 h-16 mx-auto text-white/80" />
            <h2 className="heading-2 text-white">You're Not Alone</h2>
            <p className="body-lg text-white/80">
              Whether you need practical advice, emotional support, or just someone to 
              listen, we're here for you 24/7.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/chat" data-testid="caregiver-cta-chat">
                <Button variant="secondary" className="bg-white text-primary hover:bg-white/90 rounded-full px-8 py-6 text-lg">
                  Start a Conversation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact" data-testid="caregiver-cta-contact">
                <Button variant="outline" className="border-white text-white hover:bg-white/10 rounded-full px-8 py-6 text-lg">
                  <Phone className="w-5 h-5 mr-2" />
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
